import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ITrainersData, ITrainersTool } from '@/types/trainers';
import { getTools, getTrainersContent } from '@/api/qa';
import DefaultTrainer from '@/components/ui/trainers/DefaultTrainer';
import { TrainerReviewsContextProvider } from '@/hooks/useTrainerReviews';
import { TrainerFeedbackContextProvider } from '@/hooks/useTrainerFeedback';
import UserStore from '@/utils/session';
import PositionAzimuth from '@/components/ui/trainers/PositionAzimuth';
import MosaicTrainer from '@/components/ui/trainers/MosaicTrainer';
import EnumeratorTrainerIcon from '@/components/ui/icons/trainers/EnumeratorTrainerIcon';
import MosaicTrainerIcon from '@/components/ui/icons/trainers/MosaicTrainerIcon';
import BinaryTrainerIcon from '@/components/ui/icons/trainers/BinaryTrainerIcon';
import AzimuthTrainerIcon from '@/components/ui/icons/trainers/AzimuthTrainerIcon';
import MergeTrainerIcon from '@/components/ui/icons/trainers/MergeTrainerIcon';
import PositionTrainerIcon from '@/components/ui/icons/trainers/PositionTrainerIcon';
import SelectTrainerIcon from '@/components/ui/icons/trainers/SelectTrainerIcon';
import { initCapitalize } from '@/utils/strings';
import { TightLayoutNoNav } from '@/components/layout/Layout';
import MergeTrainer from '@/components/ui/trainers/MergeTrainer';
import ImageLocation from '@/components/ui/trainers/ImageLocation';
import ImageQualityTrainer from '@/components/ui/trainers/ImageQualityTrainer';

export enum TrainerType {
  BINARY = 'binary',
  ENUMERATOR = 'enumerator',
  TEXT_ANNOTATION = 'textAnnotation',
  MOSAIC = 'mosaic',
  POINT_OF_INTEREST = 'poi',
  AZIMUTH = 'angular',
  MERGE = 'merge',
  IMAGE_LOCATION = 'imageLocation',
  POSITION_AZIMUTH = 'positionAzimuth',
  IMAGE_QUALITY = 'imageQuality',
  PICKER = 'picker',
}

export const getFilterByTrainerType = (
  activeFilter: string,
  trainerType: string,
) => {
  switch (trainerType) {
    // Enumerator, Image Location and Text Annotation share the same icon
    case TrainerType.ENUMERATOR:
    case TrainerType.IMAGE_LOCATION:
    case TrainerType.TEXT_ANNOTATION:
      return (
        activeFilter === TrainerType.ENUMERATOR ||
        activeFilter === TrainerType.TEXT_ANNOTATION ||
        activeFilter === TrainerType.IMAGE_LOCATION
      );
    case TrainerType.POSITION_AZIMUTH:
    case TrainerType.POINT_OF_INTEREST:
      return (
        activeFilter === TrainerType.POSITION_AZIMUTH ||
        activeFilter === TrainerType.POINT_OF_INTEREST
      );
    case TrainerType.IMAGE_QUALITY:
      return activeFilter === TrainerType.BINARY;
    case TrainerType.PICKER:
      return activeFilter === TrainerType.PICKER;
    default:
      return activeFilter === trainerType;
  }
};

export const getTrainerIcon = (trainerType: string) => {
  switch (trainerType) {
    // POI and PositionAzimuth share the same icon
    case TrainerType.POINT_OF_INTEREST:
    case TrainerType.POSITION_AZIMUTH:
      return PositionTrainerIcon;
    case TrainerType.MERGE:
      return MergeTrainerIcon;
    case TrainerType.AZIMUTH:
      return AzimuthTrainerIcon;
    // Enumerator, Image Location and Text Annotation share the same icon
    case TrainerType.IMAGE_LOCATION:
    case TrainerType.ENUMERATOR:
    case TrainerType.TEXT_ANNOTATION:
      return EnumeratorTrainerIcon;
    case TrainerType.MOSAIC:
      return MosaicTrainerIcon;
    case TrainerType.IMAGE_QUALITY:
    case TrainerType.BINARY:
      return BinaryTrainerIcon;
    case TrainerType.PICKER:
      return SelectTrainerIcon;
    default:
      // eslint-disable-next-line react/display-name
      return () => <></>;
  }
};

export const getTrainerIconFromTool = (tool?: ITrainersTool) => {
  const trainerType = getTrainerType(tool);
  return getTrainerIcon(trainerType);
};

export const getTrainerName = (tool?: ITrainersTool) => {
  if (!tool) return '';
  return initCapitalize(tool.name);
};

export const getTrainerLocation = (tool?: ITrainersTool) => {
  if (!tool || !tool?.location) return '';
  return tool.location === 'default' ? 'GLOBAL' : tool.location.toUpperCase();
};

export const getTrainersPage = (tool?: ITrainersTool) => {
  if (!tool) return '';
  return (
    '/trainers/' +
    tool.name.replaceAll(' ', '-').toLowerCase() +
    `?location=${tool.location}`
  );
};

export const getTrainerType = (tool?: ITrainersTool) => {
  if (!tool) return '';
  return tool.widget;
};

const TrainerPage: React.FC = () => {
  const [data, setData] = useState<ITrainersData | undefined>();
  const [tool, setTool] = useState<ITrainersTool>();
  const [tools, setTools] = useState<ITrainersTool[]>([]);
  const name = getTrainerName(tool);
  const location = getTrainerLocation(tool);
  const page = getTrainersPage(tool);
  const description = tool?.args?.description || '';
  const props = { data, name, location, page, description, tool, tools };
  const trainerType = getTrainerType(tool);
  const router = useRouter();
  useEffect(() => {
    const tryToGetTool = async () => {
      const id = router.query.id as string;
      const location = (
        (router.query.location || 'default') as string
      ).toLowerCase();
      const toolName = id.replaceAll('-', ' ').toLowerCase();

      const { tools } = await getTools();
      const tool: ITrainersTool | undefined = (tools || [])?.find(
        (t: ITrainersTool) =>
          t.name?.toLowerCase() === toolName &&
          t.location?.toLowerCase() === location,
      );
      if (!tool || tool.disabled) {
        return router.push('/trainers');
      }

      const data = await getTrainersContent(tool._id, `/trainers/${id}`);
      if (data) {
        if ('redirect' in data && data.redirect)
          setData({
            ...data,
          });
        // Important do not return other data for redirects
        else if (data.error) setData({ ...data });
        else setData({ ...data });
        setTool(tool);
        setTools(tools);
      }
    };
    tryToGetTool();
  }, []);

  let Content = <></>;

  switch (trainerType) {
    case TrainerType.PICKER:
    case TrainerType.POINT_OF_INTEREST:
      Content = (
        <PositionAzimuth
          {...props}
          modifyPosition={true}
          modifyAzimuth={false}
        />
      );
      break;
    case TrainerType.POSITION_AZIMUTH:
      Content = (
        <PositionAzimuth
          {...props}
          modifyPosition={true}
          modifyAzimuth={true}
        />
      );
      break;
    case TrainerType.MERGE:
      Content = <MergeTrainer {...props} />;
      break;
    case TrainerType.AZIMUTH:
      Content = (
        <PositionAzimuth
          {...props}
          modifyPosition={false}
          modifyAzimuth={true}
        />
      );
      break;
    case TrainerType.IMAGE_QUALITY:
      Content = <ImageQualityTrainer {...props} />;
      break;
    case TrainerType.IMAGE_LOCATION:
      Content = <ImageLocation {...props} />;
      break;
    case TrainerType.ENUMERATOR:
    case TrainerType.TEXT_ANNOTATION:
    case TrainerType.BINARY:
      Content = <DefaultTrainer {...props} />;
      break;
    case TrainerType.MOSAIC:
      Content = <MosaicTrainer {...props} />;
      break;
    default:
      break;
  }

  return (
    <TightLayoutNoNav>
      <TrainerReviewsContextProvider>
        <TrainerFeedbackContextProvider>
          {Content}
        </TrainerFeedbackContextProvider>
      </TrainerReviewsContextProvider>
    </TightLayoutNoNav>
  );
};

export default TrainerPage;

export const getServerSideProps = () => {
  const user = UserStore.get();
  const isProhibited =
    !user || (user.isEmailUser && !user.admin && !user.advancedReviewer);

  if (isProhibited) {
    return {
      redirect: {
        destination: `/connect-wallet?redirectTo=/trainers`,
        permanent: false,
      },
    };
  }

  return {
    props: {},
  };
};
